import { useState } from 'react';
import Button from '@mui/material/Button';
import EditIcon from '@mui/icons-material/Edit';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { Form, FormState } from 'informed';
import { TextArea } from '@calefy-inc/informedMaterial';
import { useAllowByAdminPermissions } from '../../../../hooks';
import { SupportLink } from '../../../common/SupportLink/SupportLink';
import { GetAllReferralCodesQuery } from '../../../../gql/graphql';
import { useMutation } from '@apollo/client';
import { graphql } from '../../../../gql';
import { useAuth } from '@calefy-inc/authentication';
import { useSnackbar } from 'notistack';
import { GET_ALL_REFERRAL_CODES } from '../../../../queries';

type ReferralCode = NonNullable<
  GetAllReferralCodesQuery['getAllReferralCodes']
>[0];

interface EditReferralCodeButtonProps {
  referralCode: ReferralCode;
}

export const EditReferralCodeButton = ({
  referralCode,
}: EditReferralCodeButtonProps) => {
  const hasAdminPermissions = useAllowByAdminPermissions();
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  if (!hasAdminPermissions) {
    return null;
  }

  return (
    <>
      <Button
        variant='outlined'
        size='small'
        startIcon={<EditIcon />}
        onClick={() => setDialogOpen(true)}
      >
        Edit
      </Button>
      <EditReferralCodeDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        referralCode={referralCode}
      />
    </>
  );
};

interface EditReferralCodeDialogProps {
  open: boolean;
  onClose: () => void;
  referralCode: ReferralCode;
}

interface EditReferralCodeErrorAlertProps {
  error?: Error;
}

const EditReferralCodeErrorAlert = ({
  error,
}: EditReferralCodeErrorAlertProps) => {
  return (
    <Collapse in={!!error}>
      <Box sx={{ mb: 2 }}>
        <Alert severity='error'>
          Sorry, there was an error updating this referral code. Please try
          again in a few minutes. If the error persists, please contact our
          dedicated support team at{' '}
          <SupportLink
            subject={`Error updating referral code: ${
              error ? error.message : 'Unknown error'
            }`}
          />
          .
        </Alert>
      </Box>
    </Collapse>
  );
};

const EditReferralCodeDialog = ({
  open,
  onClose,
  referralCode,
}: EditReferralCodeDialogProps) => {
  const { token } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [editReferralCode, { loading, error }] = useMutation(
    EDIT_REFERRAL_CODE,
    {
      refetchQueries: () => {
        if (!token) return [];
        return [{ query: GET_ALL_REFERRAL_CODES, variables: { token } }];
      },
    },
  );

  const handleSubmit = async (formState: FormState) => {
    const { values } = formState;
    const description = String(values.description || '').trim();

    if (!referralCode?.id || !token) {
      enqueueSnackbar('Missing required information', {
        variant: 'error',
      });
      return;
    }

    try {
      const result = await editReferralCode({
        variables: {
          referralCodeId: referralCode.id,
          description: description,
          token: token,
        },
      });

      if (result?.data?.editReferralCode?.referralCode) {
        enqueueSnackbar('Referral code updated successfully!', {
          variant: 'success',
        });
        onClose();
      }
    } catch (error) {
      enqueueSnackbar('Failed to update referral code', {
        variant: 'error',
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Edit Referral Code</DialogTitle>
      <Form
        onSubmit={handleSubmit}
        initialValues={{
          description: referralCode?.description || '',
        }}
      >
        <DialogContent>
          <TextArea
            fullWidth
            name='description'
            label='Description'
            rows={5}
            placeholder='Enter a description for this referral code'
          />
          <EditReferralCodeErrorAlert error={error} />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type='submit'
            variant='contained'
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

const EDIT_REFERRAL_CODE = graphql(`
  mutation EditReferralCode(
    $referralCodeId: ID!
    $description: String!
    $token: String!
  ) {
    editReferralCode(
      referralCodeId: $referralCodeId
      description: $description
      token: $token
    ) {
      referralCode {
        id
        code
        description
      }
    }
  }
`);
